import * as _ from 'lodash';
import initialState from './initialState';
// eslint-disable-next-line import/no-cycle
import { types } from './actions';
import {
  getPicklistOptionText,
  ON_TASK_TYPE_CHANGE_FOR_LINKED_TASKS,
  PICKLIST_OPTIONS,
  TASK_FORM_MODE,
} from '../lib/constants';
import { mapTaskToFormValues, setPicklistOptionsToState } from '../lib/Mapper';
// eslint-disable-next-line import/no-cycle
import { DEFAULT_TASK_SCHEMA, BULK_EDIT_SCHEMA } from '../lib/schemaConstants';

import { getActionTypes } from '../../../../../HelperMethods/UtilityFunctions';

const actionType = getActionTypes('manageTaskFormReducer')(['UPDATE_TASK', 'BULK_ACCEPT_TASK']);

export function updateTaskInReducer(task) {
  return { type: actionType.UPDATE_TASK, task };
}

export function bulkEditPageLoading() {
  return { type: actionType.LOADER_BULK_UPDATE };
}

const PARENT_TEMPLATE_KEYS_MAP = {
  assetId: ['sameLocationAs'],
  selectedStop: ['sameLocationAs'],
  equipmentIdToEvaluate: ['sameAssigneeAs', 'immediateNextTo'],
  assigneeIdsToEvaluate: ['sameAssigneeAs', 'immediateNextTo'],
};

export default function manageTaskFormReducer(state = initialState, action) {
  switch (action.type) {
    case types.LOAD_CREATE_BULK_FORM_SCHEMA_AND_VALUES: {
      const { schema, task } = action;
      const updatedSchema = Object.assign(schema, BULK_EDIT_SCHEMA);

      const formValues = mapTaskToFormValues(task, updatedSchema);
      return { ...state, formValues, schema, taskFormMode: TASK_FORM_MODE.UPDATE };
    }
    case types.BULK_ACCEPT_TASK: {
      const { acceptBulkAccept } = action;
      return { ...state, acceptBulkAccept };
    }
    case types.LOAD_CREATE_FORM_SCHEMA_AND_VALUES: {
      const { schema, task } = action;
      const formValues = mapTaskToFormValues(task, schema);
      return { ...state, formValues, schema, taskFormMode: TASK_FORM_MODE.CREATE };
    }
    case types.LOAD_CREATE_LINK_TASK_FORM_SCHEMA_AND_VALUES: {
      const { schema, task, parentLinkedTask } = action;
      schema.s_tasktype.onChange = ON_TASK_TYPE_CHANGE_FOR_LINKED_TASKS;
      schema.s_stop.disabled = true;
      schema.s_asset.disabled = true;
      const formValues = mapTaskToFormValues(task, schema);
      return {
        ...state,
        formValues,
        schema,
        parentLinkedTask,
        taskFormMode: TASK_FORM_MODE.CREATE,
        [PICKLIST_OPTIONS.ASSIGNEE]: { ...initialState[PICKLIST_OPTIONS.ASSIGNEE] },
      };
    }
    case types.LOAD_PICKLIST_FAILED: {
      const { picklistType } = action;
      return setPicklistOptionsToState(state, picklistType, false, []);
    }

    case types.LOAD_FORM_SCHEMA_AND_VALUES: {
      const { schema, task } = action;
      const updatedSchema = Object.assign(schema, DEFAULT_TASK_SCHEMA);
      const formValues = mapTaskToFormValues(task, updatedSchema);
      return { ...state, formValues, schema: updatedSchema, task, taskFormMode: TASK_FORM_MODE.UPDATE };
    }
    case types.LOAD_PICKLIST_SUCCESSFUL: {
      const { entities, picklistType } = action;

      const picklistOptions = entities.map((entity) => ({
        key: entity.id,
        value: entity.id,
        text: getPicklistOptionText(picklistType)(entity),
        originalobject: entity,
      }));

      // Sort task type options alphabetically by text
      if (picklistType === PICKLIST_OPTIONS.TASK_TYPE) {
        picklistOptions.sort((a, b) => a.text.localeCompare(b.text));
      }

      return setPicklistOptionsToState(state, picklistType, false, picklistOptions);
    }
    case types.IS_TASK_TYPE_PARENT: {
      const { taskTypeSelectedisParent } = action;
      return { ...state, taskTypeSelectedisParent };
    }
    case types.LOADING_PICKLIST: {
      const { picklistType, isSearching } = action;
      return setPicklistOptionsToState(state, picklistType, true, [], isSearching);
    }
    case types.RESET_TASK_FORM_COMPONENT: {
      return { ..._.cloneDeep(initialState) };
    }
    case types.LOAD_FORM_HISTORY: {
      const { taskHistory } = action;

      return { ...state, taskHistoryState: { items: taskHistory.taskhistory || [], isLoading: false } };
    }
    case types.LOADING_PICKLISTS: {
      const { picklistTypes } = action;
      let modifiedState = state;
      picklistTypes.forEach((picklistType) => {
        modifiedState = setPicklistOptionsToState(modifiedState, picklistType, true, []);
      });
      return modifiedState;
    }
    case types.STOP_LOADING_PICKLISTS: {
      const { picklistTypes } = action;
      let modifiedState = state;
      picklistTypes.forEach((picklistType) => {
        modifiedState = setPicklistOptionsToState(modifiedState, picklistType, false, []);
      });
      return modifiedState;
    }
    case types.LOAD_FORM_SCHEMA: {
      const { schema } = action;
      return { ...state, schema };
    }
    case types.LOADING_PICKLIST_FOR_CUSTOM_ATTRIBUTE: {
      const { attributeNames } = action;
      let { customPicklistOptions } = state;
      attributeNames.forEach((attributeName) => {
        customPicklistOptions = setPicklistOptionsToState(customPicklistOptions, attributeName, true, []);
      });
      return { ...state, customPicklistOptions };
    }
    case types.LOADED_PICKLIST_FOR_CUSTOM_ATTRIBUTE_SUCCESSFULLY: {
      const { customPicklistOptionsList } = action;
      return { ...state, customPicklistOptions: customPicklistOptionsList };
    }
    case types.LOADED_PICKLIST_FOR_CUSTOM_ATTRIBUTE_FAILED: {
      const { attributeNames } = action;
      let { customPicklistOptions } = state;
      attributeNames.forEach((attributeName) => {
        customPicklistOptions = setPicklistOptionsToState(customPicklistOptions, attributeName, false, []);
      });
      return { ...state, customPicklistOptions };
    }
    case types.LOADING_DEPENDENT_TASKS_SUCCESSFUL: {
      return { ...state, dependentTasks: { isLoading: true, items: [] } };
    }
    case types.LOADED_DEPENDENT_TASKS_SUCCESSFUL: {
      const { dependentTasks } = action;
      return {
        ...state,
        dependentTasks: {
          isLoading: false,
          items: dependentTasks,
        },
      };
    }
    case types.LOADED_DEPENDENT_TASKS_FAILED: {
      return { ...state, dependentTasks: { isLoading: false, items: [] } };
    }
    case types.LOADING_ASSET_CRITERIA: {
      return { ...state, assetCriteriaTemplate: { isLoading: true, info: {} } };
    }
    case types.LOAD_ASSET_CRITERIA_FAILED: {
      return { ...state, assetCriteriaTemplate: { isLoading: false, info: {} } };
    }
    case types.LOAD_ASSET_CRITERIA_SUCCESSFUL: {
      const { info } = action;
      return {
        ...state,
        assetCriteriaTemplate: { isLoading: false, info: { ...state.assetCriteriaTemplate.info, ...info } },
      };
    }
    case types.LOADING_LINKED_TASKS: {
      return { ...state, linkedTasks: { isLoading: true, items: [] } };
    }
    case types.LOADED_LINKED_TASKS_SUCCESSFUL: {
      const { linkedTasks } = action;
      return { ...state, linkedTasks: { items: linkedTasks, isLoading: false } };
    }
    case types.LOADED_LINKED_TASKS_FAILED: {
      return { ...state, linkedTasks: { isLoading: false, items: [] } };
    }
    case types.LOADING_TASK_TEMPLATE: {
      return { ...state, templateInfo: { isLoading: true, items: [] } };
    }
    case types.LOADED_TASK_TEMPLATE_SUCCESSFUL: {
      const { templateInfoItems } = action;
      return { ...state, templateInfo: { items: templateInfoItems, isLoading: false } };
    }
    case types.LOADED_TASK_TEMPLATE_FAILED: {
      return { ...state, templateInfo: { isLoading: false, items: [] } };
    }
    case types.LOADING_PARENT_TASK_TEMPLATE: {
      return {
        ...state,
        parentTaskTemplate: { isLoading: true, items: [] },
        showEquipmentColumnForParentTemplate: false,
        parentTaskTemplateTaskTypesDict: {},
        childTasksDictByWorkflowId: {},
      };
    }
    case types.CLEAR_PARENT_TASK_TEMPLATE: {
      return {
        ...state,
        parentTaskTemplate: { isLoading: false, items: [], showEquipmentColumn: false },
        showEquipmentColumnForParentTemplate: false,
        parentTaskTemplateTaskTypesDict: {},
        childTasksDictByWorkflowId: {},
      };
    }
    case types.LOADED_PARENT_TASK_TEMPLATE_SUCCESSFUL: {
      const {
        parentTaskTemplateItems,
        showEquipmentColumnForParentTemplate = state.showEquipmentColumnForParentTemplate,
        parentTaskTemplateTaskTypesDict = {},
        childTasksDictByWorkflowId = state.childTasksDictByWorkflowId,
      } = action;
      return {
        ...state,
        parentTaskTemplate: { items: parentTaskTemplateItems, isLoading: false },
        showEquipmentColumnForParentTemplate,
        parentTaskTemplateTaskTypesDict,
        childTasksDictByWorkflowId,
      };
    }
    case types.LOADED_PARENT_TASK_TEMPLATE_FAILED: {
      return {
        ...state,
        parentTaskTemplate: { isLoading: false, items: [] },
        showEquipmentColumnForParentTemplate: false,
        parentTaskTemplateTaskTypesDict: {},
        childTasksDictByWorkflowId: {},
      };
    }
    case types.UPDATE_PARENT_TASK_TEMPLATE_ITEM: {
      const { workflowStepId, objToMerge, refKey } = action;
      const parentTaskTemplateItemsCopy = JSON.parse(JSON.stringify(state.parentTaskTemplate.items));

      for (const item of parentTaskTemplateItemsCopy) {
        const { originalItem } = item;
        if (originalItem.workflowStepId === workflowStepId) {
          // eslint-disable-next-line guard-for-in
          for (const k in objToMerge) {
            item[k] = objToMerge[k];
          }
        }
      }

      if (refKey && PARENT_TEMPLATE_KEYS_MAP[refKey]) {
        const keysToLookFor = PARENT_TEMPLATE_KEYS_MAP[refKey];
        for (const item of parentTaskTemplateItemsCopy) {
          const { originalItem } = item;
          for (const keyToLookFor of keysToLookFor) {
            if (originalItem[keyToLookFor] === workflowStepId) {
              // eslint-disable-next-line guard-for-in
              for (const k in objToMerge) {
                item[k] = objToMerge[k];
              }
              break;
            }
          }
        }
      }
      return { ...state, parentTaskTemplate: { items: parentTaskTemplateItemsCopy, isLoading: false } };
    }
    case types.UPDATE_PARENT_TASK_TEMPLATE: {
      const { objToMerge } = action;

      const parentTaskTemplateItemsCopy = JSON.parse(JSON.stringify(state.parentTaskTemplate.items));

      for (const item of parentTaskTemplateItemsCopy) {
        // eslint-disable-next-line guard-for-in
        for (const k in objToMerge) {
          item[k] = objToMerge[k];
        }
      }

      return { ...state, parentTaskTemplate: { items: parentTaskTemplateItemsCopy, isLoading: false } };
    }

    case types.LOADED_PARENT_TASK_TEMPLATE_ITEM_ASSIGNEE_SUCCESSFUL: {
      const { assigneeOptions, workflowStepId, equipmentOptions } = action;
      const parentTaskTemplateItemsCopy = JSON.parse(JSON.stringify(state.parentTaskTemplate.items));
      const assigneeOptionsKeys = assigneeOptions.map((item) => item.key);
      const equipmentOptionsKeys = equipmentOptions.map((item) => item.key);
      for (const item of parentTaskTemplateItemsCopy) {
        const { originalItem } = item;
        if (originalItem.workflowStepId === workflowStepId) {
          item.assigneeIdsToEvaluate = (item.assigneeIdsToEvaluate || []).filter(
            (key) => assigneeOptionsKeys.indexOf(key) > -1
          );
          item.equipmentIdToEvaluate =
            item.equipmentIdToEvaluate && equipmentOptionsKeys.indexOf(item.equipmentIdToEvaluate) > -1
              ? item.equipmentIdToEvaluate
              : '';
          item.assigneeOptions = assigneeOptions;
          item.equipmentOptions = equipmentOptions;
        }
      }

      return { ...state, parentTaskTemplate: { items: parentTaskTemplateItemsCopy, isLoading: false } };
    }

    case types.LOADING_PREFERRED_LOCATION_PARENT_TASK_TEMPLATE_ITEM: {
      const { workflowStepIdAndAssetIdData = [], isSearching, isLoading } = action;
      const workflowStepIdAndAssetIdDataDict = _.keyBy(workflowStepIdAndAssetIdData, 'workflowStepId');
      const parentTaskTemplateItemsCopy = JSON.parse(JSON.stringify(state.parentTaskTemplate.items));
      for (const item of parentTaskTemplateItemsCopy) {
        const { originalItem } = item;
        const reqWorkflow = _.get(workflowStepIdAndAssetIdDataDict, `${originalItem.workflowStepId}`);
        if (reqWorkflow) {
          item.assetOptions = {
            isLoading,
            isSearching,
            items: [],
          };
        }
      }

      return { ...state, parentTaskTemplate: { items: parentTaskTemplateItemsCopy, isLoading: false } };
    }

    case types.CLEAR_PREFERRED_LOCATION_PARENT_TASK_TEMPLATE_ITEM: {
      const parentTaskTemplateItemsCopy = JSON.parse(JSON.stringify(state.parentTaskTemplate.items));
      for (const item of parentTaskTemplateItemsCopy) {
        item.assetId = '';
        item.assetOptions = {
          isLoading: false,
          isSearching: false,
          items: [],
        };
        item.selectedStop = {};
        item.equipmentOptions = [];
        item.assigneeOptions = [];
        item.assigneeIdsToEvaluate = [];
        item.equipmentIdToEvaluate = '';
      }

      return { ...state, parentTaskTemplate: { items: parentTaskTemplateItemsCopy, isLoading: false } };
    }
    case types.LOADED_PREFERRED_LOCATION_PARENT_TASK_TEMPLATE_ITEM_SUCCESSFUL: {
      const { picklistType, workflowStepIdAndAssetIdData } = action;
      const parentTaskTemplateItemsCopy = JSON.parse(JSON.stringify(state.parentTaskTemplate.items));
      const workflowStepIdAndAssetIdDataDict = _.keyBy(workflowStepIdAndAssetIdData, 'workflowStepId');
      for (const item of parentTaskTemplateItemsCopy) {
        const { originalItem } = item;
        const reqWorkflow = _.get(workflowStepIdAndAssetIdDataDict, `${originalItem.workflowStepId}`);
        if (reqWorkflow) {
          item.assetId = reqWorkflow.assetId;
          item.assetOptions = {
            isLoading: false,
            isSearching: false,
            items: (_.get(reqWorkflow, `assets`) || []).map((asset) => ({
              key: asset.id,
              value: asset.id,
              text: getPicklistOptionText(picklistType)(asset),
              originalobject: asset,
            })),
          };
          item.selectedStop = _.get(reqWorkflow, `selectedStop`) || {};
          item.assigneeIdsToEvaluate = _.get(reqWorkflow, `assigneeIdsToEvaluate`, []);
          item.equipmentIdToEvaluate = _.get(reqWorkflow, `equipmentIdToEvaluate`, '');
          item.assigneeOptions = _.get(reqWorkflow, `assigneeOptions`, []);
          item.equipmentOptions = _.get(reqWorkflow, `equipmentOptions`, []);
        }
      }

      return { ...state, parentTaskTemplate: { items: parentTaskTemplateItemsCopy, isLoading: false } };
    }

    case types.DELETED_LINKED_TASK_SUCCESSFULLY: {
      const { task } = action;
      return {
        ...state,
        linkedTasks: { items: [...state.linkedTasks.items.filter((t) => t.id !== task.id)], isLoading: false },
      };
    }
    case types.GET_STOP:
    case types.GET_STOP_FAILED:
    case types.CLEAR_STOP: {
      return { ...state, stopForSelectedAsset: {} };
    }
    case types.GET_STOP_SUCCESS: {
      const { stop } = action;
      return { ...state, stopForSelectedAsset: stop };
    }
    case types.LOADING_MANIFEST_FORM: {
      return { ...state, manifestForm: { ...state.manifestForm, isLoading: true, schema: {} } };
    }
    case types.LOAD_MANIFEST_FORM_SUCCESSFUL: {
      const { picklist, schema, data } = action;
      return { ...state, manifestForm: { picklist, schema, data, isLoading: false } };
    }
    case types.LOAD_MANIFEST_FORM_FAILED: {
      return { ...state, manifestForm: initialState.manifestForm };
    }
    case types.CLEAR_MANIFEST_FORM: {
      return { ...state, manifestForm: initialState.manifestForm };
    }
    case types.SET_AUDIT_LOG_OF_MANIFEST_FORM: {
      const { auditLog } = action;
      return { ...state, manifestForm: { ...state.manifestForm, auditLog } };
    }
    case types.UPDATE_TASK_AFTER_GENERATE_MULTIPLE: {
      const { task } = action;
      const updatedTask = { ...state.task, ...task };
      return { ...state, task: updatedTask };
    }
    case types.SET_ADHOC_FILE_UPLOADING_STATUS: {
      return { ...state, isAdhocFileUploading: action.flag };
    }
    case actionType.UPDATE_TASK: {
      const task = _.get(action, 'task', {});
      return { ...state, task };
    }
    case types.LOAD_TASK_EMAIL_HISTORY_SUCCESSFUL: {
      return { ...state, taskEmailHistory: action.taskEmailHistory };
    }
    case types.LOAD_TASK_EMAIL_HISTORY_FAILED: {
      return { ...state, taskEmailHistory: [] };
    }
    case types.LOAD_RULES_EVALUATED_SUCCESSFUL: {
      return { ...state, evaluatedRules: action.evaluatedRules };
    }
    case types.LOAD_RULES_EVALUATED_FAILED: {
      return { ...state, evaluatedRules: [] };
    }
    case types.SEARCH_PICKLIST_FOR_FORM: {
      const { updatedPicklists } = action;
      const updatedPicklistsDict = _.keyBy(updatedPicklists, 'id');
      return {
        ...state,
        searchPicklistForForm: { ...state.searchPicklistForForm, ...updatedPicklistsDict },
      };
    }
    default:
      return state;
  }
}

export const isAdhocFileUploadingSelector = (state) => state.manageTaskFormReducer.isAdhocFileUploading;
